import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react'; // Rimosso useLayoutEffect
import ClosableTabs, { type ClosableTabItem } from './ClosableTabs'; // Importa il nuovo componente Tabs e il tipo per le tab
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
  Row,
  ColumnSizingState,
  ColumnSizingInfoState,
  getSortedRowModel, // Aggiunto per l'ordinamento
  SortingState,     // Aggiunto per l'ordinamento
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useShallow } from 'zustand/react/shallow'; // Importa useShallow
import useGeoData from '../../../store/useGeoData';
import type { LayerBase, GeoDataState } from '../../../store/types'; // Rimosso GeoJSONSourceSpecification
import { GeoJsonProperties, FeatureCollection } from 'geojson'; // Rimosso Feature
import { X, ChevronDown, ChevronUp, Table as TableIcon } from 'lucide-react';
import '../styles/AttributeTablePanel.css';

// --- Helper Function: Infer Data Type ---
const inferDataType = (value: any): string => {
  if (value === null || typeof value === 'undefined') return 'Null';
  if (typeof value === 'string') return 'String';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'Integer' : 'Float';
  }
  if (typeof value === 'boolean') return 'Boolean';
  if (Array.isArray(value)) return 'Array';
  if (typeof value === 'object') return 'Object';
  return 'Unknown';
};

// --- DataTable Component ---
interface DataTableProps {
  layer: LayerBase; // Usa LayerBase
  onStatsCalculated: (stats: { recordCount: number; columnCount: number }) => void; // Callback per le statistiche
}

type TableRowData = GeoJsonProperties;

const DataTable: React.FC<DataTableProps> = ({ layer, onStatsCalculated }) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>({
      deltaOffset: 0,
      deltaPercentage: 0,
      isResizingColumn: false,
      startOffset: 0,
      startSize: 0,
      columnSizingStart: [],
  });
  const [sorting, setSorting] = useState<SortingState>([]); // Stato per l'ordinamento
  const mapInstance = useGeoData(state => state.mapInstance);
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {

    const sourceSpec = layer.sourceSpecification as any; // Cast generico per accedere a 'type', 'data', 'url'

    if (sourceSpec?.type === 'geojson' && typeof sourceSpec.data === 'object' && sourceSpec.data?.type === 'FeatureCollection') {
        // CASO 1: GeoJSON Inline
        setTableData((sourceSpec.data as FeatureCollection).features.map(f => f.properties || {}));
        setIsLoading(false);
    } else if (layer.maplibreSourceId && mapInstance && sourceSpec &&
               ((sourceSpec.type === 'geojson' && typeof sourceSpec.data === 'string') ||
                (sourceSpec.type === 'vector' && typeof sourceSpec.url === 'string'))) {
        // CASO 2: GeoJSON da URL o Vector (es. PMTiles) da URL che richiedono query a MapLibre
        setIsLoading(true);
        
        // Definisci queryOptions qui per renderlo accessibile anche nel catch
        const queryOptions: { sourceLayer?: string } = {};

        try {
          if (sourceSpec.type === 'vector' && layer.sourceLayer) {
            queryOptions.sourceLayer = layer.sourceLayer; // Usa il sourceLayer dall'oggetto layer
          } else if (sourceSpec.type === 'vector') {
          }
          
          const featuresFromMap = mapInstance.querySourceFeatures(layer.maplibreSourceId, queryOptions);
          if (featuresFromMap.length > 0) {
          }
          setTableData(featuresFromMap.map(f => f.properties || {}));
        } catch (error) {
          setTableData([]); // Resetta i dati in caso di errore
        } finally {
          setIsLoading(false);
        }
    } else {
        // CASO 3: Altri tipi di layer, configurazioni non supportate o mancanti per la tabella attributi
        setTableData([]);
        setIsLoading(false);
    }
  }, [layer, mapInstance]); // riesegui quando il layer o l'istanza della mappa cambiano

  useEffect(() => {
    // Calcola e passa le statistiche quando tableData cambia
    const currentRecordCount = tableData.length;
    const currentColumnCount = tableData.length > 0 ? Object.keys(tableData[0] || {}).length : 0;
    onStatsCalculated({ recordCount: currentRecordCount, columnCount: currentColumnCount });
  }, [tableData, onStatsCalculated]);


  const columns = useMemo<ColumnDef<TableRowData>[]>(() => {
    if (!tableData.length) return [];
    const sample = tableData[0];
    if (sample === null) return [];
    return Object.keys(sample).map((key): ColumnDef<TableRowData> => {
      const inferredType = sample ? inferDataType(sample[key]) : 'Unknown';
      return {
        accessorKey: key,
        header: ({ column }) => ( // Modificato per accedere a 'column'
          <div
            className={column.getCanSort() ? 'cursor-pointer select-none' : ''}
            onClick={column.getToggleSortingHandler()}
            title={column.getCanSort() ? (column.getNextSortingOrder() === 'asc' ? 'Ordina ascendente' : column.getNextSortingOrder() === 'desc' ? 'Ordina discendente' : 'Annulla ordinamento') : undefined}
          >
            {key}
            <span className="data-type">({inferredType})</span>
            {{
              asc: <ChevronUp size={14} className="sort-icon" />,
              desc: <ChevronDown size={14} className="sort-icon" />,
            }[column.getIsSorted() as string] ?? null}
          </div>
        ),
        cell: info => {
          const value = info.getValue();
          let displayValue: string;
          if (typeof value === 'object' && value !== null) {
            displayValue = JSON.stringify(value);
          } else {
            displayValue = String(value ?? '');
          }
          
          // Mostra tooltip solo se il testo è lungo
          const showTooltip = displayValue.length > 30;
          
          return (
            <div className="cell-content">
              {displayValue}
              {showTooltip && (
                <div className="cell-tooltip">
                  {displayValue}
                </div>
              )}
            </div>
          );
        },
        size: 150, // Dimensione di default della colonna
        enableSorting: inferredType === 'String' || inferredType === 'Integer' || inferredType === 'Float', // Abilita l'ordinamento solo per tipi specifici
        sortingFn: inferredType === 'String' ? 'alphanumeric' : inferredType === 'Integer' || inferredType === 'Float' ? 'basic' : undefined,
      };
    });
  }, [tableData]); // Dipendenza solo da tableData

  const table = useReactTable({
    data: tableData, // Usa tableData dallo stato
    columns,
    state: {
        columnSizing,
        columnSizingInfo,
        sorting, // Aggiunto stato di ordinamento
    },
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    onSortingChange: setSorting, // Handler per il cambio di ordinamento
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(), // Aggiunto per l'ordinamento
    columnResizeMode: 'onChange',
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 38,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0; // Padding originale
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  if (isLoading) {
    return <div className="loading-data">Caricamento dati tabella...</div>;
  }

  if (!tableData.length || !columns.length) {
    return <div className="no-data">Nessun dato o proprietà trovate per questo layer.</div>;
  }

  return (
      <div ref={tableContainerRef} className="table-container">
        <table style={{ width: table.getTotalSize(), tableLayout: 'fixed' }}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{ width: header.getSize() }}
                  // Aggiunta classe per lo stile dell'header qui se necessario, o via CSS globale
                >
                  <div className="header-content"> {/* Contenitore per il contenuto e l'icona di ordinamento */}
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </div>
                  <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={`resizer ${
                          header.column.getIsResizing() ? 'isResizing' : ''
                      }`}
                      style={{
                          transform: table.options.state.columnSizingInfo?.isResizingColumn === header.column.id
                            ? `translateX(${table.options.state.columnSizingInfo.deltaOffset}px)`
                            : '',
                      }}
                  />
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody style={{ position: 'relative', height: `${totalSize}px` }}>
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {virtualRows.map(virtualRow => {
              const row = rows[virtualRow.index] as Row<TableRowData>;
              return (
                <tr
                    key={row.id}
                    data-index={virtualRow.index}
                    style={{
                        position: 'absolute',
                        top: 38, // FISSO: posizione sotto header sticky
                        left: 0,
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                        width: '100%',
                    }}
                 >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} style={{ width: cell.column.getSize() }}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} colSpan={columns.length} />
              </tr>
            )}
        </tbody>
      </table>
    </div>
  );
};


// --- AttributeTablePanel Component ---
interface AttributeTablePanelProps {
  onDimensionsChange?: (dimensions: { height: number; isCollapsed: boolean }) => void;
}

const AttributeTablePanel: React.FC<AttributeTablePanelProps> = (props) => {
  const { onDimensionsChange } = props;
  // Utilizzo di useGeoData con useShallow
  const {
    openAttributeTableIds,
    dataLayers, // Corretto da geojsonLayers a dataLayers
    closeAttributeTable,
    closeAllAttributeTables,
  } = useGeoData(useShallow((state: GeoDataState) => ({
      openAttributeTableIds: state.openAttributeTableIds,
      dataLayers: state.dataLayers || [], // Corretto da geojsonLayers a dataLayers e aggiunto fallback
      closeAttributeTable: state.closeAttributeTable,
      closeAllAttributeTables: state.closeAllAttributeTables,
    }))
);

// Stato per il pannello ancorato
const [isCollapsed, setIsCollapsed] = useState(false);
const [height, setHeight] = useState(250); // Altezza iniziale, potrebbe venire da CSS var
const [isResizing, setIsResizing] = useState(false);
const panelRef = useRef<HTMLDivElement>(null); // Ref per il pannello principale

const [activeIndex, setActiveIndex] = useState(0);
const [currentTableStats, setCurrentTableStats] = useState({ recordCount: 0, columnCount: 0 });

// Funzione per gestire il cambio di tab dal nuovo componente Tabs
const handleTabSelectionChange = (index: number) => {
  setActiveIndex(index);
};

  // openLayers ora conterrà LayerBase[]
  const openLayers = useMemo((): LayerBase[] => {
    // Rimosso console.log di debug
    return openAttributeTableIds
      .map((id: string) => {
        // Utilizza dataLayers e il fallback a array vuoto se dataLayers è undefined
        return (dataLayers || []).find((layer: LayerBase) => layer.id === id);
      })
      .filter((layer): layer is LayerBase => !!layer); // Usa LayerBase
  }, [openAttributeTableIds, dataLayers]); // Corretto da geojsonLayers a dataLayers

  const activeLayer = useMemo(() => {
    return openLayers[activeIndex >= 0 && activeIndex < openLayers.length ? activeIndex : 0];
  }, [openLayers, activeIndex]);

  // Usa lo stato currentTableStats per recordCount e columnCount
  const { recordCount, columnCount } = currentTableStats;

  // Sincronizza activeIndex quando openLayers cambia
  // e resetta le statistiche se non ci sono più layer aperti o se l'activeLayer non ha dati
  useEffect(() => {
    const numOpen = openLayers.length;
    if (numOpen === 0) {
      setActiveIndex(0);
      setCurrentTableStats({ recordCount: 0, columnCount: 0 }); // Resetta stats
    } else if (activeIndex >= numOpen) {
      setActiveIndex(numOpen - 1);
    }
    // Se l'activeLayer cambia e non ha dati (o è diverso), DataTable dovrebbe aggiornare le stats
    // Questo viene gestito dalla callback onStatsCalculated
  }, [openLayers.length, activeIndex]);


// --- Handlers per Ridimensionamento Verticale ---
const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
  e.preventDefault(); // Previene selezione testo
  setIsResizing(true);
  // Aggiungi listeners globali per mousemove e mouseup
  document.addEventListener('mousemove', handleResizeMouseMove);
  document.addEventListener('mouseup', handleResizeMouseUp);
}, []); // Nessuna dipendenza iniziale

const handleResizeMouseMove = useCallback((e: MouseEvent) => {
  // Calcola la nuova altezza basata sulla posizione Y del mouse rispetto al top della viewport
  // Considera il posizionamento del pannello (in fondo alla viewport)
  const newHeight = window.innerHeight - e.clientY;
  // Applica limiti min/max all'altezza
  const minHeight = 50; // Altezza minima
  const maxHeight = window.innerHeight - 100; // Altezza massima (lascia spazio per header/mappa)
  setHeight(Math.max(minHeight, Math.min(newHeight, maxHeight)));
}, []); // Nessuna dipendenza

const handleResizeMouseUp = useCallback(() => {
  setIsResizing(false);
  // Rimuovi listeners globali
  document.removeEventListener('mousemove', handleResizeMouseMove);
  document.removeEventListener('mouseup', handleResizeMouseUp);
}, [handleResizeMouseMove]); // Dipende da handleResizeMouseMove per la rimozione

// --- Handler per Collasso ---
const toggleCollapse = () => setIsCollapsed(!isCollapsed);

// Effetto per notificare il cambio di dimensioni
useEffect(() => {
  if (onDimensionsChange) {
    onDimensionsChange({ height: isCollapsed ? 40 : height, isCollapsed });
  }
}, [height, isCollapsed, onDimensionsChange]);

// --- Handler per Chiusura ---
const closePanel = () => {
    closeAllAttributeTables();
  };

  if (openLayers.length === 0) {
    return null;
  }

  // Preparazione dei dati per il componente ClosableTabs
  const closableTabItems = useMemo((): ClosableTabItem[] => {
    return openLayers.map(layer => ({
      id: layer.id, // ID univoco per la tab, usato internamente da ClosableTabs
      label: layer.name,
      content: <DataTable key={layer.id} layer={layer} onStatsCalculated={setCurrentTableStats} />,
      onClose: () => closeAttributeTable(layer.id), // Funzione da chiamare quando si clicca la X sulla tab
      // icon: <TableIcon size={14} /> // Icona opzionale, se la si vuole mantenere
    }));
  }, [openLayers, closeAttributeTable, setCurrentTableStats]); // Aggiunte dipendenze

// Il contenitore principale è ora un semplice div
return (
 // Applica classe per collasso e stile altezza
 <div
   ref={panelRef}
   className={`attribute-table-panel ${isCollapsed ? 'collapsed' : ''} ${isResizing ? 'resizing' : ''}`}
   style={{ height: isCollapsed ? 'auto' : `${height}px` }} // Altezza dinamica o auto se collassato
 >
   {/* Handle per il ridimensionamento verticale */}
   <div
     className="resize-handle"
     onMouseDown={handleResizeMouseDown}
     title="Ridimensiona Verticalmente"
   ></div>

   <div className="panel-container">
     <div className="panel-header">
       <TableIcon size={16} className="panel-icon" />
       <span>
         Data Table {activeLayer ? ` - ${activeLayer.name}` : ''}
         {activeLayer && ` (Records: ${recordCount}, Columns: ${columnCount})`}
       </span>
       <div className="panel-header-controls">
          {/* Pulsante Collapse/Expand */}
          <button onClick={toggleCollapse} title={isCollapsed ? "Espandi" : "Collassa"} className="panel-control-button">
              {isCollapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {/* Pulsante Chiudi Pannello (ripristinato) */}
          <button
            onClick={closePanel} // Chiama closeAllAttributeTables
            title="Chiudi Pannello"
            className="panel-control-button"
          >
            <X size={16} />
          </button>
       </div>
     </div>

     {/* Applica classe 'hidden' al contenuto quando collassato */}
     <div className={`panel-content-wrapper ${isCollapsed ? 'hidden' : ''}`}>
            {/* Rimosso panel-summary */}
            <div className="panel-body">
              <ClosableTabs
                tabs={closableTabItems}
                defaultActiveTabIndex={activeIndex}
                onTabChange={handleTabSelectionChange}
                variant="primary" // o 'secondary', o omesso per default
              />
          </div>
        </div>
      </div>
   </div>
  );
};

export default AttributeTablePanel;